import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Download, Trash2, KeyRound, Crown, Lock } from "lucide-react";
import { api, errMsg, brl, usageText, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel } from "@/components/Shared";
import { Field, inputCls, btnPrimary, btnGhost } from "@/components/Form";
import MercadoPagoPay from "@/components/MercadoPagoPay";
import RedeemCode from "@/components/RedeemCode";
import AdminTestPayment from "@/components/AdminTestPayment";

const SUB = { trial: "Teste", active: "Ativa", pending: "Pendente", inactive: "Inativa", expired: "Vencida" };

function PlanCard({ p, user }) {
  const current = p.plan_id === user.plan_id;
  return (
    <div data-testid={`plan-card-${p.plan_id}`} className={`flex flex-col rounded-xl border p-5 ${current ? "border-amber-500 bg-amber-50" : "border-slate-200"}`}>
      <p className="font-display text-lg font-bold">{p.name}</p>
      <p className="mt-1 font-display text-3xl font-extrabold">{p.price ? brl(p.price) : "Grátis"}<span className="text-sm font-medium text-slate-500">{p.price ? " / 30 dias" : ""}</span></p>
      <ul className="mt-4 flex-1 space-y-1.5">{p.features.map((x) => <li key={x} className="flex gap-2 text-sm text-slate-600"><Check className="h-4 w-4 text-emerald-600" />{x}</li>)}</ul>
      {current && user.plan_expires_at && <p className="mt-3 text-sm font-semibold text-emerald-700" data-testid="plan-expires-at">Válido até {fmtDate(user.plan_expires_at)}</p>}
      <div className="mt-5">
        {p.price ? (
          <>
            <p className="mb-2 text-xs font-semibold text-slate-500" data-testid={`plan-pay-label-${p.plan_id}`}>{current && user.subscription_status === "active" ? "Renovar: soma mais 30 dias" : "Liberar 30 dias"}</p>
            <MercadoPagoPay planId={p.plan_id} />
          </>
        ) : <button data-testid={`plan-select-${p.plan_id}`} disabled className={btnPrimary}>Incluso</button>}
      </div>
    </div>
  );
}

export default function Settings() {
  const { user, refresh, signOut } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch((e) => toast.error(errMsg(e))); }, []);

  const cancelSub = async () => {
    if (!window.confirm("Cancelar a assinatura? Você mantém o plano até o fim do período já pago.")) return;
    try { const { data } = await api.post("/subscription/cancel"); toast.success(data.message); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const changePw = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/change-password", pw); toast.success("Senha alterada"); setPw({ current_password: "", new_password: "" }); } catch (err) { toast.error(errMsg(err)); }
  };
  const exportData = async () => {
    const { data } = await api.get("/me/export");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" })); a.download = "meus-dados-recursofood.json"; a.click();
  };
  const deleteAccount = async () => {
    if (!window.confirm("Excluir definitivamente sua conta e todos os dados? Esta ação não pode ser desfeita.")) return;
    try { await api.delete("/me"); await signOut(); nav("/login"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="fade-up space-y-6">
      <PageHeader eyebrow="Conta" title="Configurações" desc={`${user.name} · ${user.email}`} />
      <Panel title="Assinatura" icon={Crown} testid="subscription-panel">
        <p className="text-sm text-slate-600">Plano atual: <b data-testid="current-plan">{user.plan?.name}</b> · Status: <b>{SUB[user.subscription_status] || user.subscription_status}</b> · Uso: <b data-testid="usage-text">{usageText(user)}</b></p>
        {user.requested_plan_id && <p className="mt-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800" data-testid="pending-plan-notice">Solicitação do plano <b>{plans.find((p) => p.plan_id === user.requested_plan_id)?.name}</b> aguardando ativação pelo administrador.</p>}
        <div className="mt-5 grid max-w-3xl gap-4 md:grid-cols-2">
          {plans.map((p) => <PlanCard key={p.plan_id} p={p} user={user} />)}
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-1.5 text-xs text-slate-500"><Lock className="h-3.5 w-3.5" /> Pagamento seguro via Pix pelo Mercado Pago. Pagamento avulso, sem renovação automática: cada pagamento libera 30 dias do plano.</p>
          {user.stripe_subscription_id && !user.cancel_at_period_end && <button data-testid="cancel-subscription-btn" onClick={cancelSub} className={`${btnGhost} text-red-600`}>Cancelar assinatura</button>}
          {user.cancel_at_period_end && <p className="text-xs font-semibold text-amber-700" data-testid="cancel-scheduled-notice">Cancelamento agendado para o fim do período pago.</p>}
        </div>
      </Panel>
      <RedeemCode />
      {user.role === "admin" && <AdminTestPayment />}
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Senha de acesso" icon={KeyRound}>
          <form onSubmit={changePw} className="space-y-3">
            <Field label="Senha atual" hint="Deixe em branco se você entra apenas com Google."><input data-testid="current-password-input" type="password" className={inputCls} value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} /></Field>
            <Field label="Nova senha"><input data-testid="new-password-input" type="password" minLength={6} required className={inputCls} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} /></Field>
            <button data-testid="change-password-btn" className={btnPrimary}>Alterar senha</button>
          </form>
        </Panel>
        <Panel title="Privacidade e dados (LGPD)">
          <p className="text-sm text-slate-600">Exporte todos os seus dados ou exclua sua conta. Leia a <Link to="/privacidade" className="font-semibold text-amber-700 underline">Política de Privacidade</Link>.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button data-testid="export-data-btn" onClick={exportData} className={btnGhost}><Download className="h-4 w-4" /> Exportar meus dados</button>
            {user.role !== "admin" && <button data-testid="delete-account-btn" onClick={deleteAccount} className={`${btnGhost} border-red-200 text-red-600 hover:bg-red-50`}><Trash2 className="h-4 w-4" /> Excluir conta</button>}
          </div>
        </Panel>
      </div>
    </div>
  );
}
